import { IkeToken } from '@ikegps/core-api';
import * as IkeModel from '@ikegps/core-shared';
import { IkeUnit } from '@ikegps/core-measurement';
import { FieldActionButton, CSS as FieldActionButtonCSS } from '../../../components/button/field.action.button';
import { ImageButton } from '../../../components/button/image.button';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { DistanceInput } from '../../../components/input/distance.input';
import { CSS as DistanceInputCSS } from '../../../components/input/distance.input.style';
import { DistanceInputState } from '../../../components/input/distance.input.state';
import { Input } from '../../../components/input/input';
import { InputState } from '../../../components/input/input.state';
import { LineSwatch } from '../../../components/line.swatch';
import { ModalService } from '../../../components/modal/modal.service';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { CurrentState } from '../../../state/page.state';
import { openLineTypeModal } from '../../line.type.modal/line.type.modal';
import type { ICollectionViewController } from '../collection.view.ctrl.interface';
import type { CollectionFieldView } from './field.view';

const CSS = {
	container: 'c-Line',
	typeRow: 'c-Line__typeRow',
	typeButton: 'c-Line__typeButton',
	typeButtonEmpty: 'c-Line__typeButton--empty',
	typeLabel: 'c-Line__typeLabel',
	error: 'c-Line__error',
	lengthRow: 'c-Line__lengthRow',
	drawActions: 'c-Line__drawActions',
	vertices: 'c-Line__vertices',
	verticesHeader: 'c-Line__verticesHeader',
	verticesCount: 'c-Line__verticesCount',
	vertexRow: 'c-Line__vertexRow',
	vertexRowOpen: 'c-Line__vertexRow--open',
	vertexToggle: 'c-Line__vertexToggle',
	vertexBadge: 'c-Line__vertexBadge',
	vertexBadgeOpen: 'c-Line__vertexBadge--open',
	vertexCoords: 'c-Line__vertexCoords',
	vertexNote: 'c-Line__vertexNote',
	vertexDetail: 'c-Line__vertexDetail',
	vertexField: 'c-Line__vertexField',
	vertexFieldLabel: 'c-Line__vertexFieldLabel',
	vertexFieldValue: 'c-Line__vertexFieldValue',
	chevron: 'c-Line__chevron',
};

const ELLIPSIS = { overflow: 'hidden', 'white-space': 'nowrap', 'text-overflow': 'ellipsis' } as const;

addStyles({
	[CSS.container]: { display: 'block', width: '100%' },
	[CSS.typeRow]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(2),
		'min-width': 0,
		'margin-bottom': DefaultTheme.space(0.5),
	},
	[CSS.typeButton]: {
		flex: '1 1 auto',
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(2),
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: '2px 0',
		'min-width': 0,
		overflow: 'hidden',
		'text-align': 'left',
		color: DefaultTheme.font.color.primary,
	},
	// No swatch yet, so center the placeholder rather than leaving it offset by the empty SVG slot.
	[CSS.typeButtonEmpty]: { 'justify-content': 'center', 'text-align': 'center' },
	[CSS.typeLabel]: { 'min-width': 0, ...ELLIPSIS },
	[CSS.lengthRow]: { color: DefaultTheme.font.color.supporting, 'margin-top': '2px' },
	[CSS.error]: { color: DefaultTheme.font.color.error, 'font-size': '12px', 'margin-top': DefaultTheme.space(1) },
	[CSS.drawActions]: { display: 'flex', gap: DefaultTheme.space(2), width: '100%' },
	[`${CSS.drawActions} .${FieldActionButtonCSS.container}`]: { flex: '1 1 0', width: 'auto' },
	[CSS.vertices]: { width: '100%', display: 'flex', 'flex-direction': 'column' },
	[CSS.verticesHeader]: {
		'font-size': '11px',
		'font-weight': 700,
		'letter-spacing': '0.4px',
		'text-transform': 'uppercase',
		color: DefaultTheme.font.color.supporting,
		padding: `${DefaultTheme.space(2)} 0 ${DefaultTheme.space(1)} ${DefaultTheme.space(1)}`,
	},
	[CSS.verticesCount]: { color: DefaultTheme.color.accent, 'margin-left': DefaultTheme.space(1) },
	[CSS.vertexRow]: {
		display: 'flex',
		'align-items': 'center',
		'border-bottom': '1px solid ' + DefaultTheme.font.color.divider,
	},
	// When open, the divider moves to the bottom of the expanded detail instead.
	[CSS.vertexRowOpen]: { 'border-bottom': 'none' },
	[CSS.vertexToggle]: {
		flex: '1 1 auto',
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(2),
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: '6px 0',
		'min-width': 0,
		'text-align': 'left',
	},
	[CSS.vertexBadge]: {
		'min-width': '22px',
		height: '22px',
		'border-radius': '11px',
		background: DefaultTheme.background.lightGrey,
		color: DefaultTheme.font.color.supporting,
		'text-align': 'center',
		'font-size': '11px',
		'line-height': '22px',
		'font-variant-numeric': 'tabular-nums',
		'flex-shrink': 0,
	},
	[CSS.vertexBadgeOpen]: { background: DefaultTheme.color.accent, color: DefaultTheme.font.color.white },
	// Coords keep their full width (never ellipsised); the note preview yields/ellipsises instead.
	[CSS.vertexCoords]: { flex: '0 0 auto', 'white-space': 'nowrap', 'font-variant-numeric': 'tabular-nums' },
	[CSS.vertexNote]: {
		flex: '0 1 auto',
		'min-width': 0,
		'margin-left': 'auto',
		color: DefaultTheme.font.color.supporting,
		'font-size': '12px',
		'max-width': '120px',
		...ELLIPSIS,
	},
	[CSS.vertexDetail]: {
		width: '100%',
		padding: `2px 0 ${DefaultTheme.space(2)} ${DefaultTheme.space(6)}`,
		display: 'flex',
		'flex-direction': 'column',
		'box-sizing': 'border-box',
		'border-bottom': '1px solid ' + DefaultTheme.font.color.divider,
	},
	[CSS.vertexField]: { display: 'flex', 'align-items': 'center', gap: DefaultTheme.space(2), 'min-height': '28px' },
	[CSS.vertexFieldLabel]: {
		width: '76px',
		'flex-shrink': 0,
		'white-space': 'nowrap',
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.vertexFieldValue]: {
		flex: '1 1 auto',
		'min-width': 0,
		overflow: 'hidden',
		color: DefaultTheme.font.color.supporting,
		'font-variant-numeric': 'tabular-nums',
		'white-space': 'normal',
		'overflow-wrap': 'break-word',
	},
	// MDL textfields default to 300px and inflate this content-sized popup. Pin the note input to the
	// column, and give the distance (ft/in) inputs a small definite width so the unit labels stay visible.
	[`${CSS.vertexFieldValue} .${Input.CSS.wrapper}`]: { width: '100%', 'min-width': 0 },
	[`${CSS.vertexFieldValue} .${DistanceInputCSS.container} .${Input.CSS.wrapper}`]: { width: '56px', 'min-width': 0 },
	[CSS.chevron]: { 'font-size': '18px', color: DefaultTheme.font.color.supporting, 'flex-shrink': 0 },
});

function canUpdate(): boolean {
	return Services.Token.has(IkeToken.FEATURE_LINE_TOOL);
}

function coords(point: IkeModel.ModelLinePoint, precision: number): string {
	return `${IkeUnit.toFixed(point.latitude, precision)}, ${IkeUnit.toFixed(point.longitude, precision)}`;
}

function viewVertexField(name: T, content: MithrilVNode | string): MithrilVNode {
	return m('div', { className: CSS.vertexField }, [
		m('div', { className: CSS.vertexFieldLabel }, Translate(name)),
		m('div', { className: CSS.vertexFieldValue }, content),
	]);
}

function viewVertexReadDetail(point: IkeModel.ModelLinePoint): MithrilVNode {
	const units = Services.UserSettings.units;
	const rows: MithrilVNode[] = [
		viewVertexField(T.LAT, IkeUnit.toFixed(point.latitude, 6)),
		viewVertexField(T.LNG, IkeUnit.toFixed(point.longitude, 6)),
	];
	if (point.altitude != null) {
		rows.push(viewVertexField(T.FIELD_LOCATION_ALT, IkeUnit.display(point.altitude, units)));
	}
	if (point.accuracy != null) {
		rows.push(viewVertexField(T.FIELD_LOCATION_ACCURACY, IkeUnit.display(point.accuracy, units)));
	}
	if (point.note !== '') {
		rows.push(viewVertexField(T.FIELD_LINE_NOTE, point.note));
	}
	return m('div', { className: CSS.vertexDetail }, rows);
}

/**
 * Renders one line field, holding the field (and, when editing, its controller) so the per-vertex /
 * per-row helpers read them off `this` instead of threading them through every call. A fresh instance
 * is created per interface call; accordion state lives on the controller (`ctrl.openLineVertex`), so it
 * is per-view, cleared on reset, and available in read-only detail.
 */
class LineFieldRenderer {
	constructor(
		private readonly field: IkeModel.CollectionModelFieldLine,
		private readonly ctrl: ICollectionViewController
	) {}

	viewInline(): MithrilVNode {
		if (!this.field.hasValue()) {
			return null;
		}
		return m('div', { className: CSS.container }, [this.viewTypeDisplay(), this.viewLength()]);
	}

	viewDetail(): MithrilVNode {
		return this.viewVertices();
	}

	viewEdit(): MithrilVNode {
		// Locked line fields never reach here — the collection-view value cell renders them read-only with the
		// Pro tooltip (same FEATURE_LINE_TOOL gate), so this path assumes the token is present.
		this.registerLineCompleteness();

		const draw = CurrentState.LineDraw;
		const isDrawingThis = draw.isActive && draw.state.targetField?.id === this.field.id;

		const children: MithrilVNode[] = [];

		if (this.field.hasValue()) {
			// A line is drawn once; further points are not allowed, so no draw button here.
			// The selector stays clickable for restyling an existing line.
			children.push(this.viewTypeSelect(), this.viewLength());
		} else if (isDrawingThis) {
			// The pending type row (chosen up-front, applied on Complete) — clickable to restyle mid-draw.
			children.push(this.viewPendingTypeSelect());
			// Inline mirror of the map popup's controls: Complete (needs >=2 points) + Cancel,
			// side by side. The popup stays too, so a draw can be finished/dismissed from either.
			const canComplete = draw.state.pointCount >= 2;
			children.push(
				m('div', { className: CSS.drawActions }, [
					FieldActionButton.view({
						icon: MaterialIcon.CHECK,
						text: T.LINE_DRAW_COMPLETE,
						disabled: !canComplete,
						onclick: () => draw.complete(),
					}),
					// linking:true gives the same grey treatment as the Cancel Span Link button.
					FieldActionButton.view({
						icon: MaterialIcon.CLOSE,
						text: T.LINE_DRAW_CANCEL,
						linkingText: T.LINE_DRAW_CANCEL,
						linking: true,
						onclick: () => draw.cancel(),
					}),
				])
			);
		} else if (this.ctrl.beginLineDraw != null) {
			// Empty field: just the draw button. The type is picked in a modal when the draw starts,
			// so there is no way to leave a type-without-points half-state from here.
			children.push(
				FieldActionButton.view({
					icon: MaterialIcon.FIELD_LINE,
					text: T.FIELD_LINE_DRAW,
					// Linking owns the map too, and only one line draws at a time.
					disabled: CurrentState.CollectionLink.isLinking || draw.isActive,
					onclick: () => void this.startDraw(),
				})
			);
		}

		// Surface why save is blocked while the line is half-specified (e.g. a type chosen but not yet
		// drawn) — but don't nag while it's actively being drawn.
		if (!this.field.isComplete() && !isDrawingThis) {
			children.push(m('div', { className: CSS.error }, Translate(T.FIELD_LINE_INCOMPLETE)));
		}

		return m('div', { className: CSS.container }, children);
	}

	private lengthText(): string {
		const distance = IkeUnit.display(this.field.totalDistanceMeters, Services.UserSettings.units);
		return `${distance} · ${Translate(T.FIELD_LINE_POINTS, { count: this.field.pointCount })}`;
	}

	private viewTypeDisplay(): MithrilVNode {
		if (this.field.lineType === '') {
			return null;
		}
		const symbol = this.field.schema.symbolForTitle(this.field.lineType);
		return m('div', { className: CSS.typeRow }, [
			LineSwatch.view({ symbol }),
			m('span', { className: CSS.typeLabel }, this.field.lineType),
		]);
	}

	/** Clickable swatch + title. `pick` opens the type modal; who owns the resulting title differs per caller. */
	private viewTypeRow(title: string, pick: () => Promise<void>): MithrilVNode {
		const hasType = title !== '';
		const label = hasType ? title : Translate(T.FIELD_LINE_SELECT_TYPE);
		const buttonClass = hasType ? CSS.typeButton : `${CSS.typeButton} ${CSS.typeButtonEmpty}`;
		return m('div', { className: CSS.typeRow }, [
			m('button', { type: 'button', className: buttonClass, onclick: () => void pick() }, [
				hasType ? LineSwatch.view({ symbol: this.field.schema.symbolForTitle(title) }) : null,
				m('span', { className: CSS.typeLabel }, label),
			]),
		]);
	}

	/** Restyle an existing line: picks straight onto the field. Clearing is handled by the field-wide clear button. */
	private viewTypeSelect(): MithrilVNode {
		return this.viewTypeRow(this.field.lineType, () => this.pickType());
	}

	/** Mid-draw type row: shows/repicks the pending type; the field itself stays untouched until Complete. */
	private viewPendingTypeSelect(): MithrilVNode {
		return this.viewTypeRow(CurrentState.LineDraw.state.pendingLineType, () => this.pickPendingType());
	}

	private viewLength(): MithrilVNode {
		return m('div', { className: CSS.lengthRow }, this.lengthText());
	}

	private async pickType() {
		const chosen = await openLineTypeModal(this.field.schema, this.field.lineType);
		if (chosen != null) {
			this.field.Mutator.setLineType(chosen);
		}
	}

	private async pickPendingType() {
		const state = CurrentState.LineDraw.state;
		const chosen = await openLineTypeModal(this.field.schema, state.pendingLineType);
		if (chosen != null) {
			state.setPendingLineType(chosen);
		}
	}

	/** Modal first: pick a type, then enter draw mode with it pending. Cancelling the modal is a no-op. */
	private async startDraw() {
		if (this.ctrl.beginLineDraw == null) {
			return;
		}
		// Only one type to choose — skip the picker and start drawing immediately (UFO-968).
		const visible = this.field.schema.visibleLineTypes;
		if (visible.length === 1 && visible[0] != null) {
			this.ctrl.beginLineDraw(this.field, visible[0].title);
			return;
		}
		// '' for an empty field; preselects the type if a legacy half-state field ever reaches this button.
		const chosen = await openLineTypeModal(this.field.schema, this.field.lineType);
		if (chosen == null) {
			return;
		}
		this.ctrl.beginLineDraw(this.field, chosen);
	}

	/**
	 * Register a hidden validity gate (under a sub-id) so the pole can't be saved while the line is
	 * half-specified — mirroring the draw flow, which already refuses to commit points without a type.
	 */
	private registerLineCompleteness(): void {
		const changeState = this.ctrl.changeState;
		const id = `${this.field.id}-complete`;
		if (changeState == null || changeState.getState(id) != null) {
			return;
		}
		const state = new InputState(this.field.schema.displayName, id);
		state.isErrorShown = false;
		const errorFor = (incomplete: boolean) => (incomplete ? Translate(T.FIELD_LINE_INCOMPLETE) : null);
		state.setError(errorFor(!this.field.isComplete()));
		state.reaction(
			() => !this.field.isComplete(),
			(incomplete) => state.setError(errorFor(incomplete))
		);
		changeState.addState(id, state);
	}

	private isVertexOpen(index: number): boolean {
		return this.ctrl.openLineVertex.get(this.field.id) === index;
	}

	private toggleVertex(index: number) {
		if (this.isVertexOpen(index)) {
			this.ctrl.openLineVertex.delete(this.field.id);
		} else {
			this.ctrl.openLineVertex.set(this.field.id, index);
		}
	}

	private async deleteVertex(index: number) {
		// Dropping below 2 vertices leaves a line that can't render or be saved, so clear it entirely.
		// Warn first, then discard the whole line (both remaining points) on confirm.
		if (this.field.pointCount <= 2) {
			const confirmed = await ModalService.open({
				title: T.FIELD_LINE_DELETE_TITLE,
				content: Translate(T.FIELD_LINE_DELETE_BELOW_MIN),
				hasCancel: true,
				okName: T.CONTINUE,
			});
			if (!confirmed.ok) {
				return;
			}

			this.ctrl.openLineVertex.delete(this.field.id);
			this.ctrl.clearField(this.field);
		} else {
			this.field.Mutator.deleteVertex(index);
			// Keep any open accordion on its vertex: close only if it was the deleted one,
			// and shift the stored index down when a vertex below it was removed.
			const open = this.ctrl.openLineVertex.get(this.field.id);
			if (open === index) {
				this.ctrl.openLineVertex.delete(this.field.id);
			} else if (open != null && open > index) {
				this.ctrl.openLineVertex.set(this.field.id, open - 1);
			}
		}
	}

	/** Editable per-vertex detail (note + altitude, unit-aware). State lives in the controller's change-state. */
	private viewVertexEditDetail(point: IkeModel.ModelLinePoint, index: number): MithrilVNode {
		const altId = `${this.field.id}-alt-${index}`;
		let altState = this.ctrl.changeState.getState<number>(altId) as DistanceInputState;
		if (altState == null) {
			altState = new DistanceInputState(Translate(T.FIELD_LOCATION_ALT));
			altState.onValue(() => this.field.Mutator.setAltitude(index, altState.value));
			altState.setValue(point.altitude);
			this.ctrl.changeState.addState(altId, altState);
		} else if (!altState.meters.focused && !altState.feet.focused && !altState.inch.focused) {
			altState.setValue(point.altitude);
		}

		const noteId = `${this.field.id}-note-${index}`;
		let noteState = this.ctrl.changeState.getState<string>(noteId);
		if (noteState == null) {
			noteState = new InputState<string>(Translate(T.FIELD_LINE_NOTE));
			noteState.isLabelShown = false;
			noteState.onValue(() => this.field.Mutator.setNote(index, noteState.value ?? ''));
			noteState.setValue(point.note);
			this.ctrl.changeState.addState(noteId, noteState);
		} else if (!noteState.focused) {
			noteState.setValue(point.note);
		}

		return m('div', { className: CSS.vertexDetail }, [
			viewVertexField(T.LAT, IkeUnit.toFixed(point.latitude, 6)),
			viewVertexField(T.LNG, IkeUnit.toFixed(point.longitude, 6)),
			viewVertexField(T.FIELD_LOCATION_ALT, DistanceInput.view(altState)),
			viewVertexField(T.FIELD_LINE_NOTE, Input.view(noteState)),
		]);
	}

	private viewVertexRow(point: IkeModel.ModelLinePoint, index: number, isEditing: boolean): MithrilVNode {
		const open = this.isVertexOpen(index);
		const badgeClass = open ? `${CSS.vertexBadge} ${CSS.vertexBadgeOpen}` : CSS.vertexBadge;
		const rowClass = open ? `${CSS.vertexRow} ${CSS.vertexRowOpen}` : CSS.vertexRow;
		return m('div', {}, [
			m('div', { className: rowClass }, [
				m('button', { type: 'button', className: CSS.vertexToggle, onclick: () => this.toggleVertex(index) }, [
					Icon.view(open ? MaterialIcon.KEY_DOWN : MaterialIcon.KEY_RIGHT, { className: CSS.chevron }),
					m('span', { className: badgeClass }, String(index + 1)),
					m('span', { className: CSS.vertexCoords }, coords(point, 5)),
					point.note !== '' ? m('span', { className: CSS.vertexNote }, point.note) : null,
				]),
				isEditing
					? ImageButton.view(MaterialIcon.CLOSE, {
							onclick: () => void this.deleteVertex(index),
							testId: 'line-vertex-delete',
						})
					: null,
			]),
			open
				? m(
						'div',
						{
							// The map highlights this vertex exactly while its detail is mounted (UFO-969).
							oncreate: () => CurrentState.setFocusedLineVertex(this.field.collection.revision, this.field.id, index),
							onremove: () => CurrentState.clearFocusedLineVertex(this.field.collection.revision, this.field.id, index),
						},
						isEditing ? this.viewVertexEditDetail(point, index) : viewVertexReadDetail(point)
					)
				: null,
		]);
	}

	/** The "Vertices" dropdown — the body the info button toggles. Editable in edit mode, read-only otherwise. */
	private viewVertices(): MithrilVNode {
		const isEditing = this.ctrl.isEditing && canUpdate();
		return m('div', { className: CSS.vertices }, [
			m('div', { className: CSS.verticesHeader }, [
				Translate(T.FIELD_LINE_VERTICES),
				m('span', { className: CSS.verticesCount }, String(this.field.pointCount)),
			]),
			...this.field.points.map((point, index) => this.viewVertexRow(point, index, isEditing)),
		]);
	}
}

export const LineFieldView: CollectionFieldView<IkeModel.CollectionModelFieldLine> = {
	viewInline(field: IkeModel.CollectionModelFieldLine, ctrl: ICollectionViewController): MithrilVNode {
		return new LineFieldRenderer(field, ctrl).viewInline();
	},

	hasDetail(field: IkeModel.CollectionModelFieldLine): boolean {
		return field.hasValue();
	},

	viewDetail(field: IkeModel.CollectionModelFieldLine, ctrl: ICollectionViewController): MithrilVNode {
		return new LineFieldRenderer(field, ctrl).viewDetail();
	},

	hasClear(field: IkeModel.CollectionModelFieldLine): boolean {
		// Also clearable when only a type is set (no points) — otherwise that half-specified
		// state has no exit on surfaces without a draw button. Deliberately NOT token-gated:
		// without the Pro token only drawing is blocked, clearing stays available (UFO-967).
		return field.hasValue() || field.lineType !== '';
	},

	viewEdit(field: IkeModel.CollectionModelFieldLine, ctrl: ICollectionViewController): MithrilVNode {
		return new LineFieldRenderer(field, ctrl).viewEdit();
	},
};
